import { useCallback, useEffect, useRef, useState } from 'react';
import {
  api, ago, clock, humanise,
  type SupportContext, type SupportConversation, type SupportTicketRow,
} from '../api';
import { Icon, Panel, Plate, Toast } from '../ui';
import { TripMap } from '../TripMap';

/** One tap fills the reply box; the agent edits before sending. */
const MACROS = [
  { icon: 'call', label: 'Calling your driver', text: 'I am calling your driver right now and will update you here in a minute.' },
  { icon: 'pin_drop', label: 'Share your spot', text: 'Could you tell me exactly where you are standing — a gate number or a shop name?' },
  { icon: 'schedule', label: 'Driver on the way', text: 'Your driver is on the way. You can follow the car live on the My ride screen.' },
  { icon: 'task_alt', label: 'Sorted?', text: 'Glad that is sorted. Is there anything else I can help with?' },
];

/**
 * The support desk (docs/15-RIDE-FLOW.md §8), three columns: the queue —
 * call-backs first, then whoever has waited longest — the conversation, and
 * everything about the ride it is about. Polls every few seconds; at desk
 * volumes that is simpler than a socket, and a dropped connection can never
 * leave an agent looking at a stale queue.
 */
export function Support({ isAdmin, focus }: { isAdmin: boolean; focus?: string | null }) {
  const [view, setView] = useState<'active' | 'resolved'>('active');
  const [filter, setFilter] = useState<'all' | 'callback' | 'waiting'>('all');
  const [q, setQ] = useState('');
  const [tickets, setTickets] = useState<SupportTicketRow[] | null>(null);
  // Opened straight on a conversation when an agent messages someone from People.
  const [openId, setOpenId] = useState<string | null>(focus ?? null);
  useEffect(() => { if (focus) setOpenId(focus); }, [focus]);
  const [error, setError] = useState<string | null>(null);

  const loadQueue = useCallback(async () => {
    try {
      setTickets(await api.supportQueue(view));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the queue');
    }
  }, [view]);

  useEffect(() => {
    void loadQueue();
    const t = setInterval(() => void loadQueue(), 4000);
    return () => clearInterval(t);
  }, [loadQueue]);

  const all = tickets ?? [];
  const callbacks = all.filter((t) => t.callbackRequested && t.status !== 'resolved');
  const needle = q.trim().toLowerCase();
  const shown = all.filter((t) =>
    (filter === 'all' || (filter === 'callback' ? t.callbackRequested : t.awaitingReply)) &&
    (!needle || [t.user.name, t.user.phone, t.lastMessage, t.id, t.tripId].some((f) => f?.toLowerCase().includes(needle))));

  return (
    <>
      {callbacks.length ? (
        <div className="cb-banner">
          <span className="cb-tag"><span className="dot" /> CALL-BACK QUEUE</span>
          <span>{callbacks.length} {callbacks.length === 1 ? 'person is' : 'people are'} waiting for a call: {callbacks.slice(0, 3).map((c) => c.user.name ?? c.user.phone).join(', ')}{callbacks.length > 3 ? '…' : ''}</span>
        </div>
      ) : null}

      <div className="desk">
        <section className="desk-queue">
          <div className="desk-queue-head">
            <h2><Icon name="inbox" size={20} /> Queue</h2>
            <span className="pill">{all.length} {view === 'active' ? 'open' : 'resolved'}</span>
          </div>
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, message…" />
          <div className="chips">
            <button className={`chip ${filter === 'all' ? 'on' : ''}`} onClick={() => setFilter('all')}>All ({all.length})</button>
            <button className={`chip ${filter === 'callback' ? 'on bad' : ''}`} onClick={() => setFilter('callback')}>Call-backs ({callbacks.length})</button>
            <button className={`chip ${filter === 'waiting' ? 'on' : ''}`} onClick={() => setFilter('waiting')}>Needs reply</button>
            <button className="chip" onClick={() => setView(view === 'active' ? 'resolved' : 'active')}>{view === 'active' ? 'Resolved →' : '← Open'}</button>
          </div>
          {error ? <div className="empty">{error}</div> : null}
          {!tickets ? <div className="empty">Loading…</div> : shown.length === 0 ? (
            <div className="empty">{view === 'active' ? 'Nobody is waiting.' : 'Nothing resolved yet.'}</div>
          ) : (
            <ul className="tickets">
              {shown.map((t) => (
                <li key={t.id}>
                  <div className={`ticket ${openId === t.id ? 'on' : ''} ${t.callbackRequested && t.status !== 'resolved' ? 'cb' : ''}`} onClick={() => setOpenId(t.id)} role="button" tabIndex={0}>
                    <div className="ticket-top">
                      {t.callbackRequested && t.status !== 'resolved' ? <span className="tag bad"><Icon name="phone_callback" size={14} /> CALL-BACK · {ago(t.updatedAt)}</span>
                        : t.status === 'with_agent' ? <span className="tag live"><span className="dot" /> LIVE CHAT</span>
                        : t.status === 'resolved' ? <span className="tag ok">RESOLVED</span>
                        : <span className="tag warn">WAITING</span>}
                      <span className="mono id">#{t.id.slice(0, 6).toUpperCase()}</span>
                    </div>
                    <div className="ticket-who">
                      <b>{t.user.name ?? t.user.phone ?? 'Unknown'}</b>
                      <span className="pill">{humanise(t.user.role)}{t.tripId ? ' · on a ride' : ''}</span>
                    </div>
                    <p className={t.callbackRequested ? 'bad' : ''}>{t.lastMessage ? `“${t.lastMessage}”` : '—'}</p>
                    <div className="ticket-foot">
                      <span><Icon name={t.agentName ? 'support_agent' : 'person_off'} size={14} /> {t.agentName ?? 'Unassigned'}</span>
                      {t.callbackRequested && t.user.phone && t.status !== 'resolved' ? (
                        <a className="btn sm danger" href={`tel:${t.user.phone}`} onClick={(e) => e.stopPropagation()}><Icon name="call" size={14} /> Call now</a>
                      ) : <span className="muted">{ago(t.updatedAt)}</span>}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {openId ? (
          <Conversation key={openId} id={openId} onChanged={() => void loadQueue()} />
        ) : (
          <section className="desk-empty"><Icon name="forum" size={40} /><p>Pick a conversation from the queue.</p></section>
        )}
      </div>

      {isAdmin ? <Agents /> : null}
    </>
  );
}

function Conversation({ id, onChanged }: { id: string; onChanged: () => void }) {
  const [convo, setConvo] = useState<SupportConversation | null>(null);
  const [ctx, setCtx] = useState<SupportContext | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [toast, setToast] = useState<{ msg: string; bad?: boolean } | null>(null);
  const list = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);

  const load = useCallback(async () => {
    try {
      setConvo(await api.supportTicket(id));
    } catch {
      /* the next poll retries */
    }
  }, [id]);

  useEffect(() => {
    void load();
    // Context carries contact numbers and is audited on every read, so it is
    // fetched once per conversation opened — not on every poll.
    api.supportContext(id).then(setCtx).catch(() => setCtx(null));
    const t = setInterval(() => void load(), 3000);
    return () => clearInterval(t);
  }, [id, load]);

  // Scroll the message list only — never the page the agent is reading.
  useEffect(() => {
    const el = list.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [convo?.messages.length]);

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try {
      await fn();
      await load();
      onChanged();
      setToast({ msg: ok });
    } catch (e) {
      setToast({ msg: e instanceof Error ? e.message : 'That did not work', bad: true });
    } finally {
      setBusy(false);
      setTimeout(() => setToast(null), 3000);
    }
  };

  const send = async () => {
    const body = draft.trim();
    if (!body) return;
    setDraft('');
    await act(() => api.supportReply(id, body), 'Sent');
  };

  if (!convo) return <section className="desk-empty"><p>Loading…</p></section>;
  const closed = convo.status === 'resolved';
  const who = ctx?.user?.name ?? ctx?.user?.phone ?? humanise(ctx?.user?.role ?? 'user');
  const trip = ctx?.trip ?? null;
  const total = trip?.riders.length ?? 0;
  const boarded = trip?.riders.filter((r) => r.status === 'picked_up' || r.status === 'dropped').length ?? 0;

  return (
    <>
      <section className="desk-convo">
        <div className="convo-head">
          <div className="convo-id">
            <span className="mono">#{convo.id.slice(0, 6).toUpperCase()}</span>
            {closed ? <span className="tag ok">RESOLVED</span> : convo.status === 'with_agent' ? <span className="tag live"><span className="dot" /> LIVE</span> : <span className="tag warn">WAITING</span>}
          </div>
          <div className="convo-who">
            <span className="avatar-sm">{who.slice(0, 1).toUpperCase()}</span>
            <div>
              <b>{who}</b>
              <small>
                {humanise(ctx?.user?.role ?? '')}
                {ctx?.user?.rating != null ? <> · <Icon name="star" size={13} fill /> {ctx.user.rating.toFixed(1)}</> : null}
                {ctx?.user?.phone ? <> · <a href={`tel:${ctx.user.phone}`}>{ctx.user.phone}</a></> : null}
              </small>
            </div>
          </div>
          <div className="convo-actions">
            {ctx?.user?.phone ? <a className="btn sm" href={`tel:${ctx.user.phone}`} title="Call"><Icon name="call" size={16} /></a> : null}
            {convo.status === 'open' ? <button className="btn sm" disabled={busy} onClick={() => void act(() => api.supportClaim(id), 'It is yours')}>Take it</button> : null}
            {!closed ? <button className="btn sm go" disabled={busy} onClick={() => void act(() => api.supportResolve(id), 'Resolved')}><Icon name="check" size={16} /> Resolve</button> : null}
          </div>
          {convo.callbackRequested && !closed ? (
            <div className="convo-alert"><Icon name="phone_callback" size={16} /> Asked for a call{ctx?.user?.phone ? <> — <a href={`tel:${ctx.user.phone}`}>{ctx.user.phone}</a></> : null}</div>
          ) : null}
        </div>

        <div className="convo-msgs" ref={list}>
          {convo.messages.map((m) => (
            <div key={m.id} className={`cmsg ${m.senderKind}`}>
              {m.senderKind !== 'user' ? null : <span className="cavatar">{(m.senderName ?? who).slice(0, 2).toUpperCase()}</span>}
              {m.senderKind === 'bot' ? <span className="cavatar bot"><Icon name="smart_toy" size={16} /></span> : null}
              <div className="cbubble">
                <span className="from">{m.senderName ?? (m.senderKind === 'user' ? who : humanise(m.senderKind))} · {clock(m.sentAt)}</span>
                <span>{m.body}</span>
              </div>
              {m.senderKind === 'agent' ? <span className="cavatar agent"><Icon name="support_agent" size={16} /></span> : null}
            </div>
          ))}
        </div>

        {closed ? <p className="hint convo-closed">This conversation is resolved.</p> : (
          <div className="convo-compose">
            <div className="chips">
              {MACROS.map((m) => (
                <button key={m.label} className="chip" onClick={() => { setDraft(m.text); box.current?.focus(); }}><Icon name={m.icon} size={14} /> {m.label}</button>
              ))}
            </div>
            <form onSubmit={(e) => { e.preventDefault(); void send(); }}>
              <textarea ref={box} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={`Reply to ${who}…`} rows={3}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } }} />
              <button className="btn primary" type="submit" disabled={busy || !draft.trim()}><Icon name="send" size={17} /> Send reply</button>
            </form>
            <small className="muted">Enter to send · Shift+Enter for a new line</small>
          </div>
        )}
      </section>

      <aside className="desk-ride">
        {!ctx ? <div className="sidecard"><p className="muted small">Loading…</p></div> : trip ? (
          <>
            <section className="sidecard">
              <h3><Icon name="directions_car" size={18} /> The ride <span className={`tag ${trip.status === 'started' ? 'live' : 'ok'}`}>{humanise(trip.status).toUpperCase()}</span></h3>
              <b className="ride-route">{trip.riders[0]?.pickupAddress?.split(',')[0] ?? '—'} → {trip.riders[0]?.dropAddress?.split(',')[0] ?? '—'}</b>
              <div className="bar"><i style={{ width: `${total ? Math.round((boarded / total) * 100) : 0}%`, background: 'var(--ok)' }} /></div>
              <small className="muted">{trip.serviceDate} · {humanise(trip.direction)} · {boarded}/{total} picked up</small>
            </section>

            <section className="sidecard">
              <div className="pilot">
                <span className="avatar-sm">{(trip.driver.name ?? '?').slice(0, 1)}</span>
                <div><b>{trip.driver.name ?? 'Driver'}</b><small>{[trip.vehicle.colour, trip.vehicle.make, trip.vehicle.model].filter(Boolean).join(' ')}</small></div>
              </div>
              <Plate number={trip.vehicle.registrationNo} />
              <div className="sos-actions">
                {trip.driver.phone ? <a className="btn sm" href={`tel:${trip.driver.phone}`}><Icon name="call" size={15} /> Call driver</a> : <span />}
                {trip.position ? (
                  <a className="btn sm" target="_blank" rel="noreferrer" href={`https://www.google.com/maps?q=${trip.position.lat},${trip.position.lng}`}><Icon name="my_location" size={15} /> Where now</a>
                ) : <span className="muted small">Not moving</span>}
              </div>
            </section>

            <section className="sidecard">
              <h3>Riders ({total})</h3>
              {trip.riders.map((r) => (
                <div key={r.seq} className="siderow">
                  <span className="seat">{r.seq}</span>
                  <div>
                    <b>{r.name ?? 'Rider'}</b>
                    <small>{r.pickupAddress?.split(',')[0] ?? ''}{r.phone ? <> · <a href={`tel:${r.phone}`}>{r.phone}</a></> : null}</small>
                  </div>
                  <span className={`pill ${r.status === 'skipped' ? 'warn' : ''}`}>{humanise(r.status)}</span>
                </div>
              ))}
              <button className="btn sm block" onClick={() => setShowMap(true)}><Icon name="map" size={15} /> Show the route</button>
            </section>
          </>
        ) : (
          <section className="sidecard">
            <h3><Icon name="info" size={18} /> Not about a ride</h3>
            <p className="muted small">This conversation was opened from the account screen, not from a trip.</p>
          </section>
        )}
        <section className="sidecard">
          <h3 style={{ color: 'var(--bad)' }}>Emergency</h3>
          <a className="btn danger block" href="tel:112"><Icon name="local_police" size={17} /> Call 112</a>
        </section>
      </aside>

      {showMap && trip ? <TripMap tripId={trip.id} onClose={() => setShowMap(false)} /> : null}
      {toast ? <Toast msg={toast.msg} bad={toast.bad} /> : null}
    </>
  );
}

/** Admins add support agents here. */
function Agents() {
  const [agents, setAgents] = useState<{ id: string; full_name: string | null; phone: string | null }[] | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [toast, setToast] = useState<{ msg: string; bad?: boolean } | null>(null);

  const load = () => api.agents().then(setAgents).catch(() => setAgents([]));
  useEffect(() => { void load(); }, []);

  const add = async () => {
    try {
      await api.addAgent(phone, name);
      setName('');
      setPhone('');
      setToast({ msg: 'Agent added. They sign in here with their phone.' });
      void load();
    } catch (e) {
      setToast({ msg: e instanceof Error ? e.message : 'Could not add', bad: true });
    } finally {
      setTimeout(() => setToast(null), 4000);
    }
  };

  return (
    <Panel title="Support agents">
      <form className="setting pad" onSubmit={(e) => { e.preventDefault(); void add(); }}>
        <div className="row">
          <input type="text" placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} />
          <input type="tel" placeholder="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <button className="btn primary" type="submit" disabled={name.trim().length < 2 || phone.replace(/\D/g, '').length < 10}>
            Add agent
          </button>
        </div>
      </form>
      {agents && agents.length ? (
        <ul className="agents">
          {agents.map((a) => <li key={a.id}>{a.full_name ?? '—'} · {a.phone}</li>)}
        </ul>
      ) : <p className="hint">No agents yet.</p>}
      {toast ? <Toast msg={toast.msg} bad={toast.bad} /> : null}
    </Panel>
  );
}
